import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Users } from "lucide-react";
import ResumeUploadZone from "@/components/resumes/ResumeUploadZone";
import ResumeActionsBar from "@/components/resumes/ResumeActionsBar";
import ResumeRow from "@/components/resumes/ResumeRow";
import InviteResultsDialog from "@/components/resumes/InviteResultsDialog";
import AtsExportDialog from "@/components/resumes/AtsExportDialog";
import ScanPreviewModal from "@/components/resumes/ScanPreviewModal";
import { printCandidateSummary, printTopCandidatesSummary } from "@/components/resumes/printSummary";

export default function ResumesSection({ jobId, jobTitle }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const maxReminders =
    user?.max_reminders === null || user?.max_reminders === undefined
      ? 2
      : user.max_reminders;

  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [rescoring, setRescoring] = useState(null);
  const [reminding, setReminding] = useState(null);
  const [printingBundle, setPrintingBundle] = useState(false);

  // Score floor for "top 5 (ties included)" highlighting — the 5th-ranked
  // candidate's score becomes the cutoff; anyone tied at that score or higher
  // is treated as top-ranked.
  const topScoreFloor = (() => {
    if (!resumes.length) return null;
    const sorted = [...resumes]
      .map((r) => (typeof r.score === "number" ? r.score : -1))
      .sort((a, b) => b - a);
    return sorted[Math.min(4, sorted.length - 1)] ?? null;
  })();

  // Bulk actions
  const [topN, setTopN] = useState(5);
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [sendEmails, setSendEmails] = useState(true);
  // datetime-local string (e.g. "2026-06-15T09:00"). When set + sendEmails=true,
  // the bulk-invite payload includes scheduled_at and the backend defers send.
  const [scheduledAt, setScheduledAt] = useState("");
  const [atsOpen, setAtsOpen] = useState(null); // 'greenhouse' | 'lever' | null
  const [atsForm, setAtsForm] = useState({
    api_key: "",
    on_behalf_of: "",
    perform_as: "",
  });
  const [atsPushing, setAtsPushing] = useState(false);
  const [atsResult, setAtsResult] = useState(null);

  const fetchResumes = async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}/resumes`);
      setResumes(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResumes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => {
      const ok = /\.(pdf|docx|txt)$/i.test(f.name);
      if (!ok) toast.error(`${f.name}: only PDF, DOCX, or TXT`);
      return ok;
    });
    if (!list.length) return;
    setUploading(list.length);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f, f.name);
      try {
        const { data } = await api.post(`/jobs/${jobId}/resumes`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setResumes((prev) =>
          [...prev, data].sort((a, b) => (b.score || 0) - (a.score || 0))
        );
        toast.success(`${f.name} scored ${data.score}/100`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Failed: ${f.name}`);
      } finally {
        setUploading((u) => Math.max(0, u - 1));
      }
    }
  };

  const [scanPreview, setScanPreview] = useState(null); // {files, type}

  const handleScanFiles = (files, kind) => {
    const list = Array.from(files || []).filter((f) =>
      (f.type || "").startsWith("image/")
    );
    if (!list.length) {
      toast.error("Pick at least one image (jpg / png / webp / heic)");
      return;
    }
    setScanPreview({ files: list, type: kind === "application" ? "application" : "resume" });
  };

  const inviteTopN = async () => {
    const ids = resumes
      .filter((r) => r.candidate_email)
      .slice(0, topN)
      .map((r) => r.id);
    if (ids.length === 0) {
      toast.error("No resumes with an email to invite");
      return;
    }
    setInviting(true);
    try {
      // Convert "YYYY-MM-DDTHH:MM" (local) → ISO UTC for the backend
      let scheduled_at = null;
      if (sendEmails && scheduledAt) {
        const d = new Date(scheduledAt);
        if (!isNaN(d.getTime()) && d.getTime() > Date.now() + 60 * 1000) {
          scheduled_at = d.toISOString();
        }
      }
      const { data } = await api.post(`/jobs/${jobId}/invite-from-resumes`, {
        resume_ids: ids,
        send_emails: sendEmails,
        invite_link_base: window.location.origin,
        scheduled_at,
      });
      setInviteResult(data);
      const successCount = data.invitations.filter((x) => x.interview_id).length;
      const scheduledCount = data.invitations.filter(
        (x) => x.status === "scheduled"
      ).length;
      const emailedCount = data.invitations.filter((x) => x.email_sent).length;
      if (scheduledCount > 0) {
        const when = new Date(scheduled_at).toLocaleString();
        toast.success(`${scheduledCount} invite${scheduledCount === 1 ? "" : "s"} scheduled for ${when}`);
      } else if (sendEmails) {
        toast.success(
          `${successCount} invited · ${emailedCount} email${emailedCount === 1 ? "" : "s"} sent`
        );
      } else {
        toast.success(
          `${successCount} invitation${successCount === 1 ? "" : "s"} ready`
        );
      }
      fetchResumes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invites");
    } finally {
      setInviting(false);
    }
  };

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem("aip_token");
      const res = await fetch(
        `${API}/jobs/${jobId}/resumes/export.csv?top_n=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "resumes.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Could not export CSV");
    }
  };

  const openAts = (provider) => {
    setAtsForm({ api_key: "", on_behalf_of: "", perform_as: "" });
    setAtsResult(null);
    setAtsOpen(provider);
  };

  const pushATS = async () => {
    const provider = atsOpen;
    if (!provider) return;
    setAtsPushing(true);
    setAtsResult(null);
    try {
      const payload = {
        api_key: atsForm.api_key.trim(),
        top_n: Number(topN) || 5,
      };
      if (provider === "greenhouse")
        payload.on_behalf_of = atsForm.on_behalf_of.trim();
      if (provider === "lever") payload.perform_as = atsForm.perform_as.trim();
      const { data } = await api.post(`/jobs/${jobId}/ats/${provider}`, payload);
      setAtsResult(data);
      toast.success(`${data.pushed.length} pushed · ${data.failed.length} failed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "ATS push failed");
    } finally {
      setAtsPushing(false);
    }
  };

  const onDelete = async (r) => {
    setResumes((prev) => prev.filter((x) => x.id !== r.id));
    try {
      await api.delete(`/jobs/${jobId}/resumes/${r.id}`);
      toast.success("Resume removed");
    } catch {
      toast.error("Could not delete");
      fetchResumes();
    }
  };

  const onRescore = async (r) => {
    setRescoring(r.id);
    try {
      const { data } = await api.post(`/jobs/${jobId}/resumes/${r.id}/rescore`);
      setResumes((prev) =>
        prev
          .map((x) => (x.id === r.id ? { ...x, score: data.score } : x))
          .sort((a, b) => (b.score || 0) - (a.score || 0))
      );
      toast.success(`Re-scored ${data.score}/100`);
      fetchResumes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not re-score");
    } finally {
      setRescoring(null);
    }
  };

  const onSendReminder = async (r) => {
    setReminding(r.id);
    try {
      await api.post(`/jobs/${jobId}/resumes/${r.id}/remind`);
      toast.success(`Reminder sent to ${r.candidate_email}`);
      fetchResumes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reminder");
    } finally {
      setReminding(null);
    }
  };

  const onView = async (r) => {
    try {
      const token = localStorage.getItem("aip_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/resumes/${r.id}/file`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch {
      toast.error("Could not open file");
    }
  };

  const onOpenInterview = (r) =>
    navigate(`/jobs/${jobId}/candidates/${r.interview_id}`);

  const [cancellingSchedule, setCancellingSchedule] = useState(null);
  const onCancelSchedule = async (r) => {
    setCancellingSchedule(r.id);
    try {
      await api.post(`/applicants/${r.id}/cancel-schedule`);
      toast.success("Scheduled invite cancelled");
      fetchResumes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel schedule");
    } finally {
      setCancellingSchedule(null);
    }
  };

  const onPrintSummary = async (r) => {
    const t = toast.loading("Generating candidate PDF…");
    try {
      await printCandidateSummary(r, jobTitle);
      toast.success("Candidate summary downloaded", { id: t });
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate PDF", { id: t });
    }
  };

  const onPrintTopN = async () => {
    const top = [...resumes]
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, topN);
    if (!top.length) {
      toast.error("No résumés to print yet");
      return;
    }
    setPrintingBundle(true);
    const t = toast.loading(`Bundling top ${top.length} summaries…`);
    try {
      await printTopCandidatesSummary(top, jobTitle);
      toast.success(`Top ${top.length} shortlist downloaded`, { id: t });
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate bundled PDF", { id: t });
    } finally {
      setPrintingBundle(false);
    }
  };

  return (
    <section data-testid="resumes-section" className="mt-12">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-medium">Resume ranking</h2>
          <p className="mt-1 text-sm text-slate-900">
            Upload candidate resumes (PDF / DOCX / TXT). Our AI scores each one
            against this role and ranks them top to bottom.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          <Users className="h-3 w-3" />
          {resumes.length} {resumes.length === 1 ? "applicant" : "applicants"}
        </div>
      </div>

      {resumes.length > 0 && (
        <ResumeActionsBar
          topN={topN}
          setTopN={setTopN}
          inviting={inviting}
          onInviteTopN={inviteTopN}
          sendEmails={sendEmails}
          setSendEmails={setSendEmails}
          scheduledAt={scheduledAt}
          setScheduledAt={setScheduledAt}
          onExportCSV={exportCSV}
          onPrintTopN={onPrintTopN}
          printingBundle={printingBundle}
          onOpenAts={openAts}
        />
      )}

      <ResumeUploadZone
        onFiles={handleFiles}
        uploading={uploading}
        onScanResume={(files) => handleScanFiles(files, "resume")}
        onScanApplication={(files) => handleScanFiles(files, "application")}
      />

      <div className="mt-6 border border-slate-200">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
        ) : resumes.length === 0 ? (
          <div
            data-testid="resumes-empty"
            className="p-10 text-center text-sm text-slate-500"
          >
            No resumes uploaded yet.
          </div>
        ) : (
          <ul>
            {[...resumes]
              .sort((a, b) => {
                // If at least one resume has a weighted_score, sort by it desc.
                // Falls back to resume.score so non-interviewed candidates
                // are still ordered sensibly.
                const aw =
                  typeof a.interview_weighted_score === "number"
                    ? a.interview_weighted_score * 10
                    : a.score ?? -1;
                const bw =
                  typeof b.interview_weighted_score === "number"
                    ? b.interview_weighted_score * 10
                    : b.score ?? -1;
                return bw - aw;
              })
              .map((r, i) => (
              <ResumeRow
                key={r.id}
                resume={r}
                index={i}
                expanded={expanded === r.id}
                onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
                onView={onView}
                onRescore={onRescore}
                onDelete={onDelete}
                onOpenInterview={onOpenInterview}
                onSendReminder={onSendReminder}
                onCancelSchedule={onCancelSchedule}
                onPrintSummary={onPrintSummary}
                rescoring={rescoring}
                reminding={reminding}
                cancellingSchedule={cancellingSchedule}
                maxReminders={maxReminders}
                topScoreFloor={topScoreFloor}
              />
            ))}
          </ul>
        )}
      </div>

      <InviteResultsDialog
        result={inviteResult}
        onClose={() => setInviteResult(null)}
      />

      <AtsExportDialog
        provider={atsOpen}
        onClose={() => setAtsOpen(null)}
        form={atsForm}
        setForm={setAtsForm}
        pushing={atsPushing}
        result={atsResult}
        onSubmit={pushATS}
        topN={topN}
      />

      {scanPreview && (
        <ScanPreviewModal
          jobId={jobId}
          files={scanPreview.files}
          type={scanPreview.type}
          onClose={() => setScanPreview(null)}
          onCommitted={(row) => {
            setResumes((prev) =>
              [...prev, row].sort((a, b) => (b.score || 0) - (a.score || 0))
            );
            setScanPreview(null);
          }}
        />
      )}
    </section>
  );
}
